import { createFileRoute } from "@tanstack/react-router";
import { Controller } from "react-hook-form";
import {
  isOutOfIterations,
  useBillingStatus,
} from "../components/BillingStatusContext";
import { Button } from "../components/Button";
import { ExpandImagesModal } from "../components/ExpandImagesModal";
import { Header } from "../components/Header";
import { ImageSelectorDisplay } from "../components/ImageDisplay";
import { OutOfIterationsOverlay } from "../components/OutOfIterations";
import { PromptPanel } from "../components/PromptPanel";
import { type ResultsPageState, useResultsPage } from "../hooks/useResultsPage";
import type { AsyncError } from "../lib/errors";
import { redirectHomeOnInvalidSearch } from "../lib/route-guards";
import { resultsSearchSchema } from "../lib/route-schemas";

export const Route = createFileRoute("/results")({
  validateSearch: resultsSearchSchema,
  onError: redirectHomeOnInvalidSearch,
  component: ResultsPage,
});

function ResultsMainContent({
  page,
  outOfIterations,
}: {
  page: ResultsPageState;
  outOfIterations: boolean;
}) {
  return (
    <div className="relative flex flex-1 gap-6 px-6 py-6 min-h-0 overflow-auto max-w-[1800px] w-full mx-auto">
      {/* While the overlay is up the content behind it is inert: the overlay
          swallows mouse clicks, and inert + aria-hidden keeps keyboard and
          screen-reader users off the controls behind it. */}
      <div
        inert={outOfIterations}
        aria-hidden={outOfIterations}
        className="flex flex-1 gap-6 min-h-0"
      >
        <Controller
          control={page.control}
          name="prompt"
          render={({ field: { value, onChange } }) => (
            <PromptPanel
              prompt={value}
              onPromptChange={onChange}
              isRegenerating={page.isRegenerating}
              isInitialLoading={page.isInitialLoading}
              onRegenerateImages={page.handleInitialRegenerate}
              uploadedFiles={page.uploadedFiles ?? null}
              referenceImagePreviews={page.referenceImagePreviews ?? null}
            />
          )}
        />
        <div className="flex-1 flex flex-col min-h-0">
          {page.generationError && !page.isInitialLoading && (
            <GenerationErrorBanner error={page.generationError} />
          )}
          <ImageSelectorDisplay
            images={page.images || []}
            selectedImageIndex={page.selectedImageIndex}
            isRegenerating={page.isRegenerating}
            isInitialLoading={page.isInitialLoading}
            onSelectImage={page.setSelectedImageIndex}
            onEnterRefinementMode={page.handleEnterRefinementMode}
          />
          <div className="flex justify-start pt-3 flex-shrink-0 mx-auto w-full max-w-[calc((100vh-200px)*3/2)]">
            <Button
              type="button"
              variant="primary"
              onClick={() => page.setIsExpandModalOpen(true)}
              disabled={
                !page.images?.length ||
                page.isRegenerating ||
                page.isInitialLoading
              }
            >
              Expand Images
            </Button>
          </div>
          <ExpandImagesModal
            isOpen={page.isExpandModalOpen}
            onClose={() => page.setIsExpandModalOpen(false)}
            images={page.images || []}
            initialIndex={page.selectedImageIndex ?? 0}
          />
        </div>
      </div>
      {outOfIterations && <OutOfIterationsOverlay />}
    </div>
  );
}

function GenerationErrorBanner({ error }: { error: AsyncError }) {
  if (!error) return null;
  return (
    <div className="mb-4 p-4 bg-[var(--accent-destructive)] border border-[var(--stroke-destructive)] rounded-lg">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-[var(--text-primary)]"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-label="Error"
            role="img"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Generation failed
          </h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {error.message}
          </p>
        </div>
      </div>
    </div>
  );
}

function ResultsPage() {
  const { sessionId, requestId, prompt, projectId } = Route.useSearch();
  const page = useResultsPage({
    sessionId,
    requestId,
    originalPrompt: prompt,
    projectId,
  });
  const { billingStatus, isBillingLoading } = useBillingStatus();
  const outOfIterations = isOutOfIterations(billingStatus, isBillingLoading);

  return (
    <div className="h-dvh bg-[var(--bg-default)] flex flex-col">
      <Header currentPage="results" />
      <ResultsMainContent page={page} outOfIterations={outOfIterations} />
    </div>
  );
}
